import { useState } from "react";
import DSCard from "../../../../system-design/DSCard";
import DSCardContent from "../../../../system-design/DSCardContent";
import DSEnumSelect, {
  DSEnumSelectOption,
} from "../../../../system-design/DSEnumSelect";
import DSButton from "../../../../system-design/DSButton";
import DSBox from "../../../../system-design/DSBox";
import DSTypography from "../../../../system-design/DSTypography";
import DSSectionHeading from "../../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../../system-design/sectionCardSx";
import { DeviceLogSeverity } from "../../../../../api/admin";
import useGetDeviceErrorLogs from "./hooks/useGetDeviceErrorLogs";
import DeviceErrorsTable from "./DeviceErrorsTable";

const PERIOD_OPTIONS: DSEnumSelectOption<string>[] = [
  { value: "1 DAY", label: "1 Day" },
  { value: "2 DAY", label: "2 Days" },
  { value: "7 DAY", label: "1 Week" },
  { value: "30 DAY", label: "1 Month" },
];

const SEVERITY_OPTIONS: DSEnumSelectOption<DeviceLogSeverity>[] = [
  { value: DeviceLogSeverity.ALL, label: "All" },
  { value: DeviceLogSeverity.ERROR, label: "Error" },
  { value: DeviceLogSeverity.CRITICAL, label: "Critical" },
];

const DeviceErrorsCard = ({ deviceId }: { deviceId: string }) => {
  const [period, setPeriod] = useState("1 DAY");
  const [severity, setSeverity] = useState<DeviceLogSeverity>(
    DeviceLogSeverity.ALL,
  );
  const [submitted, setSubmitted] = useState<{
    period: string;
    severity: DeviceLogSeverity;
  } | null>(null);

  const { data, error, isFetching } = useGetDeviceErrorLogs(
    deviceId,
    submitted?.period,
    submitted?.severity,
  );

  return (
    <DSCard
      className="card-stats"
      sx={[
        sectionCardSx,
        {
          mb: 2,
          minWidth: 0,
          maxWidth: "100%",
        },
      ]}
    >
      <DSSectionHeading>Error Logs</DSSectionHeading>
      <DSCardContent>
        <DSBox
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
          }}
        >
          <DSEnumSelect
            value={period}
            options={PERIOD_OPTIONS}
            onChange={setPeriod}
          />
          <DSEnumSelect
            value={severity}
            options={SEVERITY_OPTIONS}
            onChange={setSeverity}
          />

          <DSButton
            sx={{ width: 120 }}
            variant="contained"
            size="small"
            disabled={isFetching}
            onClick={() => setSubmitted({ period, severity })}
          >
            {isFetching ? "Loading..." : "Load Logs"}
          </DSButton>
        </DSBox>

        {error && (
          <DSTypography variant="body2" color="error" sx={{ mt: 1 }}>
            Failed to load logs{error.message ? `: ${error.message}` : ""}
          </DSTypography>
        )}

        {data && data.length > 0 && <DeviceErrorsTable data={data} />}

        {data && data.length === 0 && (
          <DSTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No error logs found
          </DSTypography>
        )}
      </DSCardContent>
    </DSCard>
  );
};

export default DeviceErrorsCard;
